import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GiftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import styles from '../components/enquiries/Enquiries.module.css';

const ReferJeweller = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.container}>
      {/* Header matching other pages */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1 className={styles.headerTitle}>Refer a jeweller</h1>
            <p className={styles.headerSubtitle}>
              If you know another jeweller who might benefit from Boutee, you can refer them and receive a thank-you from us when they join.
            </p>
          </div>
        </div>
      </div>

      {/* Primary Offer Card */}
      <div style={{
        background: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-xl)',
        boxShadow: 'var(--shadow-card)',
        border: 'var(--card-border)',
        marginBottom: 'var(--spacing-l)'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-l)' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: 'var(--radius-s)',
            backgroundColor: 'var(--Purple-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <GiftIcon style={{ width: '24px', height: '24px', color: 'var(--Purple-700)' }} />
          </div>
          
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: 'var(--font-size-subtitle1)',
              fontWeight: 'var(--font-weight-subtitle1)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-s)'
            }}>
              Give a jeweller 20% off | Receive £50
            </h3>
            
            <p style={{
              fontSize: 'var(--font-size-body3)',
              lineHeight: 'var(--line-height-body3)',
              color: 'var(--Text-Secondary)',
              marginBottom: 'var(--spacing-m)'
            }}>
              When you refer another jeweller to Boutee:
            </p>
            
            <ul style={{
              listStyleType: 'none',
              padding: 0,
              margin: '0 0 var(--spacing-m) 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-xs)'
            }}>
              <li style={{
                fontSize: 'var(--font-size-body3)',
                lineHeight: 'var(--line-height-body3)',
                color: 'var(--Text-Secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)'
              }}>
                <CheckCircleIcon style={{ width: '20px', height: '20px', color: 'var(--Green-600)', flexShrink: 0 }} />
                They receive 20% off when they join
              </li>
              <li style={{
                fontSize: 'var(--font-size-body3)',
                lineHeight: 'var(--line-height-body3)',
                color: 'var(--Text-Secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)'
              }}>
                <CheckCircleIcon style={{ width: '20px', height: '20px', color: 'var(--Green-600)', flexShrink: 0 }} />
                You receive £50 as a thank-you from us
              </li>
            </ul>
            
            <p style={{
              fontSize: 'var(--font-size-body4)',
              color: 'var(--Text-Tertiary)',
              margin: 0,
              fontStyle: 'italic'
            }}>
              This is our way of rewarding jewellers who help grow Boutee.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div style={{
        background: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-xl)',
        boxShadow: 'var(--shadow-card)',
        border: 'var(--card-border)',
        marginBottom: 'var(--spacing-l)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-subtitle2)',
          fontWeight: 'var(--font-weight-subtitle2)',
          color: 'var(--Text-Primary)',
          margin: '0 0 var(--spacing-l) 0',
          paddingBottom: 'var(--spacing-m)',
          borderBottom: '2px solid var(--Stroke-Light)'
        }}>
          How referrals work
        </h3>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-l)'
        }}>
          {/* Step 1 */}
          <div style={{ display: 'flex', gap: 'var(--spacing-m)', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--Purple-100)',
              color: 'var(--Purple-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              flexShrink: 0
            }}>
              1
            </div>
            <div>
              <h4 style={{
                fontSize: 'var(--font-size-body2)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xxs)'
              }}>
                Share Boutee
              </h4>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                color: 'var(--Text-Secondary)',
                margin: 0,
                lineHeight: '1.6'
              }}>
                Tell another jeweller about Boutee, or share the overview from the Share Boutee page.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{ display: 'flex', gap: 'var(--spacing-m)', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--Purple-100)',
              color: 'var(--Purple-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              flexShrink: 0
            }}>
              2
            </div>
            <div>
              <h4 style={{
                fontSize: 'var(--font-size-body2)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xxs)'
              }}>
                Ask them to mention your name
              </h4>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                color: 'var(--Text-Secondary)',
                margin: 0,
                lineHeight: '1.6'
              }}>
                When they get in touch or sign up, they need to explicitly say that you referred them.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div style={{ display: 'flex', gap: 'var(--spacing-m)', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--Purple-100)',
              color: 'var(--Purple-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              flexShrink: 0
            }}>
              3
            </div>
            <div>
              <h4 style={{
                fontSize: 'var(--font-size-body2)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xxs)'
              }}>
                They join Boutee
              </h4>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                color: 'var(--Text-Secondary)',
                margin: 0,
                lineHeight: '1.6'
              }}>
                Once they complete signup and become an active jeweller, their discount is applied.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div style={{ display: 'flex', gap: 'var(--spacing-m)', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--Purple-100)',
              color: 'var(--Purple-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              flexShrink: 0
            }}>
              4
            </div>
            <div>
              <h4 style={{
                fontSize: 'var(--font-size-body2)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xxs)'
              }}>
                You receive £50
              </h4>
              <p style={{
                fontSize: 'var(--font-size-body3)',
                color: 'var(--Text-Secondary)',
                margin: 0,
                lineHeight: '1.6'
              }}>
                We'll contact you to arrange your referral thank-you.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div style={{
        background: 'var(--Blue-50)',
        border: '1px solid var(--Blue-200)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-xl)',
        marginBottom: 'var(--spacing-l)',
        boxShadow: 'var(--shadow-card)'
        }}>
        <div style={{ display: 'flex', gap: 'var(--spacing-s)', alignItems: 'center', marginBottom: 'var(--spacing-m)' }}>
            <div style={{
            width: '36px',
            height: '36px',
            borderRadius: 'var(--radius-s)',
            background: 'var(--Blue-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--Blue-700)',
            flexShrink: 0
            }}>
            <ExclamationTriangleIcon style={{ width: '20px', height: '20px' }} />
            </div>
            <h4 style={{
            fontSize: 'var(--font-size-body3)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--Text-Primary)',
            margin: 0
            }}>
            A few things to know
            </h4>
        </div>
        
        <ul style={{
            listStyleType: 'disc',
            paddingLeft: 'var(--spacing-l)',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xs)'
        }}>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Primary)', lineHeight: '1.6' }}>
            Referrals are based on trust and clear attribution
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Primary)', lineHeight: '1.6' }}>
            The new jeweller must mention your name when joining
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Primary)', lineHeight: '1.6' }}>
            Discounts and rewards can't be combined with other offers
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Primary)', lineHeight: '1.6' }}>
            If there's ever any uncertainty, just message us
            </li>
        </ul>
      </div>

      {/* Link to Share Page */}
      <div style={{
        background: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-xl)',
        boxShadow: 'var(--shadow-card)',
        border: 'var(--card-border)',
        marginBottom: 'var(--spacing-l)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--spacing-l)',
        flexWrap: 'wrap'
      }}>
        <div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle1)',
            fontWeight: 'var(--font-weight-subtitle1)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xs)'
          }}>
            Need something to send?
          </h3>
          <p style={{
            fontSize: 'var(--font-size-body3)',
            color: 'var(--Text-Secondary)',
            margin: 0,
            lineHeight: '1.6'
          }}>
            If a jeweller asks what Boutee is, the Share Boutee page has an overview you can send - no explaining needed.
          </p>
        </div>
        <button
          onClick={() => navigate('/share-boutee')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-xs)',
            padding: 'var(--spacing-s) var(--spacing-xl)',
            fontSize: 'var(--font-size-button)',
            fontWeight: 'var(--button-font-weight)',
            fontFamily: 'var(--font-family-primary)',
            borderRadius: 'var(--button-radius)',
            border: 'none',
            cursor: 'pointer',
            transition: 'var(--transition-all)',
            boxShadow: 'var(--shadow-sm)',
            background: 'var(--Button-Secondary-Enabled-BG)',
            color: 'var(--Button-Secondary-Text)',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--Button-Secondary-Hovered-BG)', e.currentTarget.style.transform = 'translateY(-1px)', e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--Button-Secondary-Enabled-BG)', e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = 'var(--shadow-sm)')}
        >
          Go to Share Boutee
          <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
        </button>
      </div>

      {/* Referral Guidance */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 'var(--spacing-m)',
        marginBottom: 'var(--spacing-l)'
      }}>
        {/* Box 1: Who should you refer? */}
        <div style={{
          background: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xl)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)'
        }}>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)',
            margin: '0 0 var(--spacing-s) 0'
          }}>
            Who should you refer?
          </h3>
          <p style={{
            fontSize: 'var(--font-size-body3)',
            color: 'var(--Text-Secondary)',
            lineHeight: '1.6',
            margin: '0 0 var(--spacing-m) 0'
          }}>
            Boutee is designed for independent jewellers specialising in bespoke work. Referrals work best when the jeweller is a good fit for the platform.
          </p>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--Text-Primary)',
            margin: '0 0 var(--spacing-s) 0'
          }}>
            A referred jeweller should:
          </p>
          <ul style={{
            listStyleType: 'disc',
            paddingLeft: 'var(--spacing-l)',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xs)'
          }}>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              Be an independent jeweller
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              Offer bespoke or made-to-order services
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              Offer engagement rings and have a portfolio of engagement work
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              Be open to working with qualified enquiries online
            </li>
          </ul>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            color: 'var(--Text-Tertiary)',
            margin: 'var(--spacing-m) 0 0 0',
            fontStyle: 'italic'
          }}>
            If you’re unsure, just ask us - we’re happy to advise.
          </p>
        </div>

        {/* Box 2: Referral terms */}
        <div style={{
          background: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xl)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)'
        }}>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)',
            margin: '0 0 var(--spacing-s) 0'
          }}>
            Referral terms
          </h3>
          <p style={{
            fontSize: 'var(--font-size-body3)',
            color: 'var(--Text-Secondary)',
            lineHeight: '1.6',
            margin: 0
          }}>
            To keep referrals fair and simple:
          </p>
          <ul style={{
            listStyleType: 'disc',
            paddingLeft: 'var(--spacing-l)',
            margin: 'var(--spacing-m) 0 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xs)'
          }}>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              The referred jeweller must mention your name when joining
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              They must be new to Boutee
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              Rewards are issued once the jeweller has joined and is active
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              Discounts and rewards can’t be combined with other offers
            </li>
            <li style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)', lineHeight: '1.6' }}>
              We reserve the right to determine eligibility in cases of uncertainty
            </li>
          </ul>
        </div>
      </div>

      {/* Optional FAQ Section */}
      <div style={{
        background: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-xl)',
        boxShadow: 'var(--shadow-card)',
        border: 'var(--card-border)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-subtitle2)',
          fontWeight: 'var(--font-weight-subtitle2)',
          color: 'var(--Text-Primary)',
          margin: '0 0 var(--spacing-l) 0',
          paddingBottom: 'var(--spacing-m)',
          borderBottom: '2px solid var(--Stroke-Light)'
        }}>
          Common questions
        </h3>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-l)'
        }}>
          <div>
            <h4 style={{
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              Can I refer more than one jeweller?
            </h4>
            <p style={{
              fontSize: 'var(--font-size-body3)',
              color: 'var(--Text-Secondary)',
              margin: 0,
              lineHeight: '1.6'
            }}>
              Yes. You will receive £50 for each jeweller you refer who joins.
            </p>
          </div>

          <div>
            <h4 style={{
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              When do I receive the £50?
            </h4>
            <p style={{
              fontSize: 'var(--font-size-body3)',
              color: 'var(--Text-Secondary)',
              margin: 0,
              lineHeight: '1.6'
            }}>
              Once the referred jeweller has joined and is active. The money will be sent to your connected stripe account (link one via the dashboard if you haven't).
            </p>
          </div>

          <div>
            <h4 style={{
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              What if two people refer the same jeweller?
            </h4>
            <p style={{
              fontSize: 'var(--font-size-body3)',
              color: 'var(--Text-Secondary)',
              margin: 0,
              lineHeight: '1.6'
            }}>
              This is very unlikely. We'll go by who the new jeweller says referred them.
            </p>
          </div>

          <div>
            <h4 style={{
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              Can I refer someone who's already in touch with Boutee?
            </h4>
            <p style={{
              fontSize: 'var(--font-size-body3)',
              color: 'var(--Text-Secondary)',
              margin: 0,
              lineHeight: '1.6'
            }}>
              No, you must refer a jeweller who hasn't yet contacted us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferJeweller;